import React, { useMemo } from 'react';
import { Check, Copy, FileWarning, Image as ImageIcon, Info, Loader2, Play, RefreshCw, Search, Trash2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { getUserDisplayLabel, shortUserId } from '../../utils/userDisplay';

const REVIEW_LABELS = {
  clear: 'Clear',
  needs_review: 'Needs Review',
  investigating: 'Investigating',
  resolved: 'Resolved',
  false_positive: 'False Positive',
};

const THEME_LABELS = {
  noir: '1940s Noir',
  '90s_nyc': '90s NYC',
  miami_vice: '80s Miami',
};

const reviewTone = (status) => {
  switch (status) {
    case 'needs_review':
      return 'bg-amber-900/50 text-amber-300 border border-amber-700';
    case 'investigating':
      return 'bg-sky-900/50 text-sky-300 border border-sky-700';
    case 'resolved':
      return 'bg-emerald-900/50 text-emerald-300 border border-emerald-700';
    case 'false_positive':
      return 'bg-purple-900/50 text-purple-300 border border-purple-700';
    default:
      return 'bg-gray-800 text-gray-300 border border-gray-700';
  }
};

const severityTone = (severity) => {
  switch (severity) {
    case 'critical':
      return 'bg-red-900/70 text-red-200 border border-red-600';
    case 'high':
      return 'bg-red-900/50 text-red-300 border border-red-700';
    case 'medium':
      return 'bg-orange-900/50 text-orange-300 border border-orange-700';
    case 'low':
      return 'bg-blue-900/50 text-blue-300 border border-blue-700';
    default:
      return 'bg-gray-800 text-gray-400 border border-gray-700';
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'Unknown';
  let normalizedDate = dateString;
  if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+')) {
    normalizedDate = `${dateString}Z`;
  }
  return new Date(normalizedDate).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const shortId = (id) => (id ? `...${String(id).slice(-8)}` : '-');

const selectClassName = 'h-9 rounded-md border border-gray-700 bg-gray-950 px-3 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-amber-500';

const SelectFilter = ({ label, value, onChange, children }) => (
  <label className="flex min-w-[150px] flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
    {label}
    <select className={selectClassName} value={value || ''} onChange={(event) => onChange(event.target.value)}>
      {children}
    </select>
  </label>
);

const getCaseId = (caseItem) => caseItem.case_id || caseItem.id;
const getSessionId = (caseItem) => caseItem.session_id || caseItem.sessionId;
const getVictimName = (caseItem) => caseItem.victim?.name || caseItem.victim_name || 'Unknown victim';

const getHealth = (caseItem) => caseItem.health || {};

const getSignalCount = (caseItem, key) => Number(getHealth(caseItem)[key] ?? caseItem[key] ?? 0);

export const AdminCasesTable = ({
  cases = [],
  total = 0,
  loading = false,
  filters = {},
  onFiltersChange,
  onRefresh,
  onViewCaseInfo,
  onCopyId,
  copiedId,
  onLoadCase,
  onDeleteCase,
  loadingSession,
  deletingSession,
}) => {
  const visibleCount = cases.length;
  const hasFilters = Boolean(
    filters.review_status ||
    filters.theme ||
    filters.difficulty ||
    filters.user_id
  );

  const statusCounts = useMemo(() => {
    return cases.reduce((counts, caseItem) => {
      const status = getHealth(caseItem).review_status || 'clear';
      counts[status] = (counts[status] || 0) + 1;
      return counts;
    }, {});
  }, [cases]);

  const updateFilter = (key, value) => {
    onFiltersChange?.({
      ...filters,
      [key]: value,
      offset: 0,
    });
  };

  const clearFilters = () => {
    onFiltersChange?.({
      review_status: '',
      theme: '',
      difficulty: '',
      user_id: '',
      limit: filters.limit || 100,
      offset: 0,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Admin Cases</h2>
          <p className="text-sm text-gray-400">
            {loading ? 'Loading cases...' : `Showing ${visibleCount} of ${total || visibleCount} cases`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(statusCounts).map(([status, count]) => (
            <Badge key={status} className={`${reviewTone(status)} text-xs`}>
              {REVIEW_LABELS[status] || status}: {count}
            </Badge>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            className="border-gray-700 text-gray-200"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="rounded-lg border-gray-700 bg-gray-900/60 shadow-none">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-end gap-3">
            <SelectFilter label="Review" value={filters.review_status} onChange={(value) => updateFilter('review_status', value)}>
              <option value="">All</option>
              <option value="clear">Clear</option>
              <option value="needs_review">Needs review</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="false_positive">False positive</option>
            </SelectFilter>
            <SelectFilter label="Theme" value={filters.theme} onChange={(value) => updateFilter('theme', value)}>
              <option value="">All</option>
              <option value="noir">1940s Noir</option>
              <option value="90s_nyc">90s NYC</option>
              <option value="miami_vice">80s Miami</option>
            </SelectFilter>
            <SelectFilter label="Difficulty" value={filters.difficulty} onChange={(value) => updateFilter('difficulty', value)}>
              <option value="">All</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </SelectFilter>
            <label className="flex min-w-[220px] flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
              User ID
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-600" />
                <Input
                  value={filters.user_id || ''}
                  onChange={(event) => updateFilter('user_id', event.target.value)}
                  placeholder="created_by_user_id"
                  className="border-gray-700 bg-gray-950 pl-8 text-gray-200 placeholder:text-gray-600"
                />
              </div>
            </label>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-400 hover:text-white">
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="rounded-lg border border-gray-700 bg-gray-900/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-800/60">
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Case</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Review</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Signals</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Theme</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Difficulty</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Created By</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Created</th>
                <th className="p-3 text-right text-xs font-medium uppercase tracking-wide text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {loading ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-gray-400">
                    <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />
                    Loading admin cases
                  </td>
                </tr>
              ) : cases.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-gray-500">
                    No cases match the current filters.
                  </td>
                </tr>
              ) : cases.map((caseItem) => {
                const caseId = getCaseId(caseItem);
                const sessionId = getSessionId(caseItem);
                const health = getHealth(caseItem);
                const reviewStatus = health.review_status || 'clear';
                const imageTotal = getSignalCount(caseItem, 'image_total');
                const imageFailures = getSignalCount(caseItem, 'image_failures');
                const generationFailures = getSignalCount(caseItem, 'generation_failure_count');
                const isLoadingSession = loadingSession === sessionId;
                const isDeletingSession = deletingSession === sessionId;
                const createdByLabel = getUserDisplayLabel({
                  id: caseItem.created_by_user_id,
                  displayName: caseItem.created_by_display_name,
                });

                return (
                  <tr key={caseId || sessionId} className="hover:bg-gray-800/40 transition-colors">
                    <td className="p-3 align-top">
                      <div className="font-medium text-white">{getVictimName(caseItem)}</div>
                      <button
                        type="button"
                        onClick={() => caseId && onCopyId?.(caseId, 'case')}
                        className="mt-1 flex items-center gap-1 font-mono text-xs text-gray-500 hover:text-gray-300"
                        title={caseId || ''}
                      >
                        C: {shortId(caseId)}
                        {copiedId === caseId ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                      </button>
                      {sessionId && (
                        <button
                          type="button"
                          onClick={() => onCopyId?.(sessionId, 'session')}
                          className="mt-1 flex items-center gap-1 font-mono text-xs text-gray-600 hover:text-gray-300"
                          title={sessionId}
                        >
                          S: {shortId(sessionId)}
                          {copiedId === sessionId ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                        </button>
                      )}
                    </td>
                    <td className="p-3 align-top">
                      <div className="flex flex-wrap gap-1">
                        <Badge className={`${reviewTone(reviewStatus)} text-xs`}>
                          {REVIEW_LABELS[reviewStatus] || reviewStatus}
                        </Badge>
                        {health.review_severity && (
                          <Badge className={`${severityTone(health.review_severity)} text-xs`}>
                            {health.review_severity}
                          </Badge>
                        )}
                      </div>
                      {health.review_issue_tags?.length > 0 && (
                        <div className="mt-2 flex max-w-[240px] flex-wrap gap-1">
                          {health.review_issue_tags.map((tag) => (
                            <Badge key={tag} variant="outline" className="border-gray-700 text-gray-400 text-[11px]">
                              {tag.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="p-3 align-top">
                      <div className="flex flex-wrap gap-1">
                        <Badge className={generationFailures > 0
                          ? 'bg-red-900/50 text-red-300 border border-red-700 text-xs'
                          : 'bg-gray-800 text-gray-400 border border-gray-700 text-xs'}
                        >
                          <FileWarning className="mr-1 h-3 w-3" />
                          {generationFailures}
                        </Badge>
                        <Badge className={imageFailures > 0
                          ? 'bg-orange-900/50 text-orange-300 border border-orange-700 text-xs'
                          : 'bg-gray-800 text-gray-400 border border-gray-700 text-xs'}
                        >
                          <ImageIcon className="mr-1 h-3 w-3" />
                          {imageFailures}/{imageTotal}
                        </Badge>
                      </div>
                    </td>
                    <td className="p-3 align-top">
                      <Badge className="bg-gray-800 text-gray-200 border border-gray-700">
                        {THEME_LABELS[caseItem.theme] || caseItem.theme || 'unknown'}
                      </Badge>
                    </td>
                    <td className="p-3 align-top text-sm capitalize text-gray-300">
                      {caseItem.difficulty || '-'}
                    </td>
                    <td className="p-3 align-top">
                      <span
                        className={`text-xs text-gray-400 ${caseItem.created_by_display_name ? '' : 'font-mono'}`}
                        title={caseItem.created_by_user_id || ''}
                      >
                        {caseItem.created_by_display_name ? createdByLabel : shortUserId(caseItem.created_by_user_id)}
                      </span>
                    </td>
                    <td className="p-3 align-top text-sm text-gray-400">
                      {formatDateTime(caseItem.created_at || caseItem.started_at)}
                    </td>
                    <td className="p-3 align-top">
                      <div className="flex justify-end gap-1">
                        {onLoadCase && sessionId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onLoadCase(sessionId)}
                            disabled={isLoadingSession || isDeletingSession}
                            className="h-8 px-2 text-gray-400 hover:text-white hover:bg-gray-700"
                            title="Load session"
                          >
                            {isLoadingSession ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                          </Button>
                        )}
                        {onViewCaseInfo && caseId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onViewCaseInfo(caseId, sessionId)}
                            className="h-8 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
                            title="View case info"
                          >
                            <Info className="h-4 w-4" />
                          </Button>
                        )}
                        {caseId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onCopyId?.(caseId, 'case')}
                            className="h-8 px-2 text-gray-400 hover:text-white hover:bg-gray-700"
                            title="Copy case ID"
                          >
                            {copiedId === caseId ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                          </Button>
                        )}
                        {onDeleteCase && sessionId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteCase(sessionId, getVictimName(caseItem))}
                            disabled={isLoadingSession || isDeletingSession}
                            className="h-8 px-2 text-red-500 hover:text-red-400 hover:bg-red-900/30"
                            title="Delete session"
                          >
                            {isDeletingSession ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminCasesTable;
